'use client';

import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { createDefaultSettlePolicyRegistry } from '../../core/dispatch-policy';
import type { SettlePolicyRegistry } from '../../core/dispatch-policy';
import { ReadinessController } from '../../core/readiness';
import type { BaselineDigitalData, UserContextInput } from '../../types/digital-data';
import { LaunchTransport } from '../browser/launch-transport';
import { AnalyticsProvider } from './AnalyticsProvider';
import type { CoordinatorDebugConfig } from './AnalyticsProvider';

export type LaunchAnalyticsProviderProps = {
  baseline?: BaselineDigitalData;
  settlePolicy?: SettlePolicyRegistry;
  isDebugEnabled?: boolean;
  shouldRegisterAsSingleton?: boolean;
  coordinatorDebugConfig?: CoordinatorDebugConfig;
  userContext: UserContextInput | null;
  children: ReactNode;
};

/**
 * Creates a Launch transport and provides an analytics coordinator via AnalyticsProvider.
 * Marks readiness after the metrics-ready event when the transport is callable.
 */
export function LaunchAnalyticsProvider({
  baseline,
  settlePolicy,
  isDebugEnabled = false,
  shouldRegisterAsSingleton = false,
  coordinatorDebugConfig,
  userContext,
  children,
}: LaunchAnalyticsProviderProps) {
  const defaultSettle = useMemo(() => createDefaultSettlePolicyRegistry(), []);
  const transport = useMemo(() => new LaunchTransport(defaultSettle), [defaultSettle]);
  transport.setDebugEnabled(isDebugEnabled);
  transport.setSettleRegistry(settlePolicy ?? defaultSettle);
  const readinessInstance = useMemo(
    () => new ReadinessController({ isTransportReady: () => transport.isCallable() }),
    [transport]
  );

  useEffect(() => {
    function handleMetricsReady() {
      if (transport.isCallable()) {
        readinessInstance.markReady();
      }
    }

    window.addEventListener('metrics-ready', handleMetricsReady);
    handleMetricsReady();
    return () => window.removeEventListener('metrics-ready', handleMetricsReady);
  }, [readinessInstance, transport]);

  return (
    <AnalyticsProvider
      transport={transport}
      baseline={baseline}
      isDebugEnabled={isDebugEnabled}
      shouldRegisterAsSingleton={shouldRegisterAsSingleton}
      coordinatorDebugConfig={coordinatorDebugConfig}
      userContext={userContext}
      readinessInstance={readinessInstance}
    >
      {children}
    </AnalyticsProvider>
  );
}
